import type { jsPDF } from 'jspdf';

import type { DrawPDFBookmarkInstruction, DrawPDFLinkInstruction } from '../../../rendering';

interface EntryOptions {
  pageNumber: number;
}

type LinkEntry = [DrawPDFLinkInstruction, EntryOptions];
type BookmarkEntry = [DrawPDFBookmarkInstruction, EntryOptions];

type AddLinkOptions = { url: string } | { pageNumber: number; top?: number };

/**
 * Since links in PDFs can link to bookmarks on other pages, this helper object keeps track of all
 * links and bookmarks and adds them to the PDF once all pages have been rendered.
 */
export class PDFLinks {
  private pdf: jsPDF;
  private links = new Set<LinkEntry>();
  private bookmarks = new Map<string, BookmarkEntry>();

  constructor(pdf: jsPDF) {
    this.pdf = pdf;
  }

  addLink(drawInstruction: DrawPDFLinkInstruction, options: EntryOptions) {
    this.links.add([drawInstruction, options]);
  }

  addBookmark(drawInstruction: DrawPDFBookmarkInstruction, options: EntryOptions) {
    this.bookmarks.set(drawInstruction.name, [drawInstruction, options]);
  }

  createLinks() {
    for (const [drawInstruction, { pageNumber }] of this.links) {
      const { height, width, x, y } = drawInstruction;
      const options = this.getLinkOptions(drawInstruction);
      if (!options) continue;

      this.pdf.setPage(pageNumber);
      this.pdf.link(x, y, width, height, options);
    }
  }

  private getLinkOptions({ destination, linkType }: DrawPDFLinkInstruction): AddLinkOptions | null {
    if (linkType === 'url') return { url: destination };

    const bookmarkEntry = this.bookmarks.get(destination);
    if (!bookmarkEntry) return null;

    const [{ y }, { pageNumber }] = bookmarkEntry;
    return { pageNumber, top: y };
  }
}
